import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Container,
  Row,
  Col,
  Input,
  Button,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import AdminHeader from "components/Headers/AdminHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import { getCertificatePendingReport } from "action/report";
import { certificatePendingReport } from "constants/report";

function CertificatePendingReport() {
  const initialFilters = {
    course: "",
    trainee: "",
    batch: "",
  };

  const dispatch = useDispatch();

  const certificatePendingReportData = useSelector(
    (state) => state.report.certificatePendingReport
  );

  const [filters, setFilters] = useState(initialFilters);

  const [filterState, setFilterState] = useState({
    filterParams: [],
  });

  const [pageNum, setPageNum] = useState(1);

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  useEffect(() => {
    getCertificatePendingReport(
      dispatch,
      pageNum,
      filterState.filterParams
    );

    return () => {
      dispatch({
        type: certificatePendingReport,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (certificatePendingReportData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          certificatePendingReportData?.data?.certificatePendingReport
            ?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (
      certificatePendingReportData?.code ||
      certificatePendingReportData?.message
    ) {
      setListState({
        isLoading: false,
        error:
          certificatePendingReportData?.message ||
          "Failed to load pending certificate report.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [certificatePendingReportData]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.course.trim()) {
      filterParams.push({
        field: "CourseName",
        condition: "like",
        filterValues: [filters.course.trim()],
      });
    }

    if (filters.trainee.trim()) {
      filterParams.push({
        field: "TraineeName",
        condition: "like",
        filterValues: [filters.trainee.trim()],
      });
    }

    if (filters.batch.trim()) {
      filterParams.push({
        field: "BatchName",
        condition: "like",
        filterValues: [filters.batch.trim()],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((previous) => ({
        ...previous,
        isLoading: true,
        error: "",
        showLoaderOnClick: true,
      }));

      setFilterState({
        filterParams: newFilterParams,
      });

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setListState((previous) => ({
      ...previous,
      isLoading: true,
      error: "",
      showLoaderOnClick: false,
    }));

    setFilters(initialFilters);

    setFilterState({
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 80,
      centerAlign: true,
    },
    {
      Header: "Trainee Name",
      accessor: "traineeName",
      minWidth: 180,
      maxWidth: 230,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Trainee Email",
      accessor: "traineeEmail",
      minWidth: 200,
      maxWidth: 250,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Trainee Mobile",
      accessor: "traineeMobile",
      minWidth: 140,
      maxWidth: 160,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Course",
      accessor: "course",
      minWidth: 220,
      maxWidth: 280,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
    {
      Header: "Batch",
      accessor: "batch",
      minWidth: 200,
      maxWidth: 280,
      Cell: ({ value }) => (
        <span className="text-wrapper">{value || "-"}</span>
      ),
    },
  ];

  const tableData = (
    certificatePendingReportData?.data?.certificatePendingReport || []
  ).map((item, index) => ({
    ...item,
    id: `${item?.traineeID}-${item?.courseID}-${item?.batchID}`,
    srNo:
      (pageNum - 1) *
        (certificatePendingReportData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
    traineeName: item?.traineeName || "-",
    traineeEmail: item?.traineeEmail || "-",
    traineeMobile: item?.traineeMobile || "-",
    course: item?.courseName || "-",
    batch: item?.batchName || "-",
  }));

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Pending Certificates Report
                </CardTitle>
              </CardHeader>

              <CardBody>
                {/* Filters */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      name="trainee"
                      placeholder="Search by trainee name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.trainee}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />
                    
                    <Input
                      type="text"
                      name="course"
                      placeholder="Search by course"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.course}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      name="batch"
                      placeholder="Search by batch"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.batch}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      disabled={listState.isLoading}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${
                        listState.showLoaderOnClick ? "search-btn" : ""
                      }`}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={clearSearch}
                        disabled={listState.isLoading}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Record Count */}
                <div className="mt-2 mb-3 mx-1">
                  {certificatePendingReportData?.data?.totalRecords > 0 && (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing{" "}
                          {certificatePendingReportData?.data
                            ?.totalFilteredRecords || 0}{" "}
                          of{" "}
                          {certificatePendingReportData?.data?.totalRecords}{" "}
                          Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records:{" "}
                          {certificatePendingReportData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Table States */}
                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation" />
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords || tableData.length === 0 ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info" />
                    </div>
                    <span>No pending certificate records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData}
                      defaultPageSize={
                        certificatePendingReportData?.data
                          ?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE
                      }
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        (certificatePendingReportData?.data
                          ?.totalFilteredRecords || 0) /
                          (certificatePendingReportData?.data
                            ?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE)
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default CertificatePendingReport;
